import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import './styles.css';
import useLoading from "../../../../Hooks/useLoading";
import { AuthContext } from "../../../../Contexts/AuthContext";
import { CompanyContext } from "../../../../Contexts/CompanyContext";
import { LinesModel } from "../../../../Models/LinesModel";
import { CreateProjectGroupingLinesAPI, CreateProjectLinesAPI, GetAllLinesNotAssignedByProjectGroupingIdAPI, GetAllNotAssignedLinesByProjectIdAPI } from "../../../../API/RecommendationAPI";
import { IResponseProps } from "../../../../Interfaces/IResponseProps";
import { AutoComplete, Button, Input, Modal, Table, TableColumnsType } from "antd";
import { TableRowSelection } from "antd/es/table/interface";

interface ShowModalProps {
    show: boolean,
    toggleShow: () => void,
    projectId?: string,
    projectGroupingId?: string,
    setValueHasChanged: React.Dispatch<React.SetStateAction<boolean>>,
    companyId: string
}

interface ISelectAddProjectHelper {
    page: number,
    rows: string[]
}

const ModalLines = ({ show, toggleShow, projectId, projectGroupingId, setValueHasChanged, companyId }: ShowModalProps) => {
    const { loading, toggleLoading } = useLoading();
    const { userEmail } = useContext(AuthContext);
    const { company } = useContext(CompanyContext);
    const [lines, setLines] = useState<LinesModel[]>([]);
    const [search, setSearch] = useState<string>("");
    const [total, setTotal] = useState<number>(0);
    const [take, setTake] = useState<number>(7);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selecteds, setSelecteds] = useState<ISelectAddProjectHelper[]>([]);
    const [page, setPage] = useState<number>(1);

    const getAllLinesNotAssigned = async (page?: number) => {
        try {
            toggleLoading(true);
            setPage(page ?? 1);

            let text = search == null || search == "" ? "all" : search;
            if (company.potential === 0) {
                await GetAllNotAssignedLinesByProjectIdAPI(projectId ?? "", companyId, page ?? 1, take, text).then((resp: IResponseProps) => {
                    if (resp.statusCode == 200) {
                        setLines(resp.data.items);
                        setTotal(resp.data.total);
                        setSelectedRowKeys(selecteds.find(x => x.page == page)?.rows ?? []);
                    }
                }).finally(() => {
                    toggleLoading(false);
                });
            }
            else {
                await GetAllLinesNotAssignedByProjectGroupingIdAPI(projectGroupingId ?? "", companyId, page ?? 1, take, text).then((resp: IResponseProps) => {
                    if (resp.statusCode == 200) {
                        setLines(resp.data.items);
                        setTotal(resp.data.total);
                        setSelectedRowKeys(selecteds.find(x => x.page == page)?.rows ?? []);
                    }
                }).finally(() => {
                    toggleLoading(false);
                });
            }

        } catch (error: any) {
            toast.error(error);
            toggleLoading(false);
        }
    }

    const onSelectChange = (newSelectedRowKeys: React.Key[], selectedRow: LinesModel[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        let exist = selecteds.find(x => x.page == page);
        if (exist != undefined) {
            var newArray = selecteds;
            let removeInArray = exist?.rows.filter(x => !newSelectedRowKeys.includes(x));
            let addInArray = newSelectedRowKeys.filter(x => !exist?.rows.includes(x.toString())).map(x => x.toString());

            if (addInArray) {
                newArray.find(x => x.page == page)?.rows.push(...addInArray);

                setSelecteds(newArray);
            }
            
            if(removeInArray) {
                let rows = newArray.find(x => x.page == page)?.rows.filter(x => !removeInArray.includes(x));
                newArray = newArray.filter(x => x.page != page);
                newArray.push({
                    page: page,
                    rows: rows ?? []
                })

                setSelecteds(newArray);
            }
        }
        else {
            setSelecteds(prev => [...prev, { page: page, rows: selectedRow.map(x => x.id) }]);
        }
    };

    const CreateProjectLines = async () => {
        try {
            toggleLoading(true);

            let allSelecteds = selecteds.map(x => x.rows.map(y => y)).flat();

            if (company.potential === 0) {
                await CreateProjectLinesAPI(projectId ?? "", userEmail, allSelecteds, companyId).then((resp: IResponseProps) => {
                    if (resp.statusCode == 200) {
                        toast.success("Linhas atribuidas ao projeto!");
                        setValueHasChanged(true);
                        toggleShow();
                    }
                }).finally(() => toggleLoading(false));
            }
            else {
                await CreateProjectGroupingLinesAPI(projectGroupingId ?? "", userEmail, allSelecteds, companyId).then((resp: IResponseProps) => {
                    if (resp.statusCode == 200) {
                        toast.success("Linhas atribuidas ao agrupamento!");
                        setValueHasChanged(true);
                        toggleShow();
                    }
                }).finally(() => toggleLoading(false));
            }
        } catch (error: any) {
            toggleLoading(false);
            toast.error(error);
        }
    }

    useEffect(() => {
        if (show) {
            getAllLinesNotAssigned();
        } else {
            setSearch("");
            setSelecteds([]);
            setSelectedRowKeys([]);
        }
    }, [show, take]);

    const columns: TableColumnsType<LinesModel> = [
        {
            title: 'favoritesGrantsName',
            dataIndex: 'favoritesGrantsName',
        },
    ];

    const rowSelection: TableRowSelection<LinesModel> = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    return (
        <Modal
            title="Selecione os projetos para este Agrupamento"
            onCancel={toggleShow}
            open={show}
            width={800}
            footer={null}
            centered
            closable={true}
            className='modal-container'
        >
            <div className="mt-4">
                <AutoComplete
                    popupMatchSelectWidth={true}
                    className="w-100 pe-0"
                    onSearch={(val) => { setSearch(val) }}
                >
                    <Input.Search showCount={false} className="white" loading={loading} placeholder="Procurar" enterButton />
                </AutoComplete>
                <Table<LinesModel>
                    loading={loading}
                    rowKey="id"
                    showHeader={false}
                    pagination={{
                        pageSizeOptions: ['7', '10', '25', '50', '75', '100'],
                        showSizeChanger: true,
                        total: total,
                        showTotal: (total) => `Total ${total} items`,
                        pageSize: take,
                        onChange: (page, pageSize) => {
                            getAllLinesNotAssigned(page)
                        },
                        onShowSizeChange: (page, size) => {
                            setTake(size)
                        }
                    }}
                    className="mt-2"
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={lines}
                />
            </div>
            <div className='btns-delete-modal'>
                <Button
                    type='default'
                    color="default"
                    onClick={toggleShow}
                    className='btn-modal-cancel'
                >
                    Cancelar
                </Button>
                <Button
                    type='primary'
                    color="primary"
                    className='btn-modal-confirm'
                    disabled={selecteds.filter(x => x.rows.length > 0).length == 0}
                    onClick={CreateProjectLines}
                >
                    Confirmar
                </Button>
            </div>
        </Modal>
    )
}

export default ModalLines;